import { useCallback } from "react";
import useSWR, { KeyedMutator } from "swr";

import authenticatedGigApiFetcher from "./fetching";
import ApiError, { isApiError } from "./apiError";
import { useAcquireAccessToken } from "../../auth/useAccessToken";
import { useNavigate } from "react-router-dom";
import { fourOFourPath } from "../../Routes";
import { fourOThreePath } from "../../Routes";

export type UseGiggedApiSWRReturn<T> = {
    data?: T,
    isLoading: boolean
    isValidating: boolean
    error?: ApiError
    /** Revalidates the key; returns the same promise as SWR mutate (for sequencing after server-side fixes). */
    refetch: () => ReturnType<KeyedMutator<T>>
    mutate: KeyedMutator<T>
}

export type UseGiggedApiSWROptions = {
    disableAutoReValidation?: boolean,
    preventRedirectOn404?: boolean
}

export const useGiggedApiSWR = <T,>(url: string | null, options?: UseGiggedApiSWROptions): UseGiggedApiSWRReturn<T> => {
    const { acquireAccessToken } = useAcquireAccessToken();
    const navigate = useNavigate();

    const fetcher = async (url: string) => {
        try {
            const accessToken = await acquireAccessToken();
            return await authenticatedGigApiFetcher<unknown, T>({url, method: "GET", body: undefined, token: accessToken});
        }
        catch (error) {
            if (isApiError(error) && error.statusCode === 404 && !options?.preventRedirectOn404) {
                navigate(fourOFourPath);
            }
            if (isApiError(error) && error.statusCode === 403 && !window.location.pathname.startsWith("/403")) {
                navigate(fourOThreePath);
            }

            console.error(`Unhandled error making request to ${url}:\n`, error);
            throw error;
        }
    };

    const swrOptions = options?.disableAutoReValidation ?
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        } :
        undefined;

    const { data, error, mutate, isValidating } = useSWR<T, ApiError>(
        url !== null ? url : null,
        fetcher,
        swrOptions);

    // Must be referentially stable: callers often pass refetch into useEffect dependency arrays.
    const refetch = useCallback(() => mutate(), [mutate]);

    return {
        data,
        isLoading: !error && !data,
        isValidating,
        error,
        refetch,
        mutate
    };
};